import { motion } from 'framer-motion';
import { Heart, Sparkles, Stars, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WelcomeStepProps {
  onStart: () => void;
}

const WelcomeStep = ({ onStart }: WelcomeStepProps) => {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="max-w-2xl"
      >
        {/* Decorative element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-center gap-2 mb-6"
        >
          <Stars className="w-5 h-5 text-gold" />
          <span className="text-sm tracking-[0.3em] uppercase text-muted-foreground font-medium">
            Bienvenidos
          </span>
          <Stars className="w-5 h-5 text-gold" />
        </motion.div>

        {/* Main heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-serif text-4xl sm:text-5xl md:text-6xl text-foreground mb-6 leading-tight"
        >
          Creemos juntos la invitación
          <span className="block text-primary mt-2">
            de tu boda perfecta
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto leading-relaxed"
        >
          En dos pasos tendrás tu invitación lista: los nombres y la fecha, y el estilo que más
          los represente. Luego afinarás cada detalle en el editor.
        </motion.p>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-4 mb-10"
        >
          {[
            { icon: Heart, text: '7 estilos únicos' },
            { icon: Sparkles, text: 'Preview real al instante' },
            { icon: Stars, text: 'Lista en minutos' },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2 bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50 shadow-sm"
            >
              <item.icon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{item.text}</span>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            size="lg"
            onClick={onStart}
            className="text-lg px-8 py-6 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all bg-gradient-to-r from-primary to-primary/90 group"
          >
            Comenzar ahora
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 text-xs text-muted-foreground"
        >
          No te preocupes, podrás modificar todo después ✨
        </motion.p>
      </motion.div>

      {/* Decorative hearts */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ delay: 1 }}
        className="absolute bottom-20 left-1/4"
      >
        <Heart className="w-8 h-8 text-primary fill-primary/30" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ delay: 1.2 }}
        className="absolute top-1/3 right-1/4"
      >
        <Heart className="w-6 h-6 text-primary fill-primary/20" />
      </motion.div>
    </div>
  );
};

export default WelcomeStep;
